import { useNavigate } from 'react-router-dom';

import { Avatar, Box, Divider, Menu, MenuItem, Stack, Typography } from '@mui/material';

import { ROUTES, ROUTES_PERMISSIONS } from 'constants/index';
import { getUserDisplayName, getUserInitials, getUserRoleLabel } from 'helpers/userPresentation';
import { useSession } from 'providers/SessionProvider';
import { MdLogout, MdPersonOutline, MdSettings } from 'react-icons/md';

type Props = {
  handleClose: () => void;
  anchorEl: null | HTMLElement;
  open: boolean;
};

export default function UserMenu({ handleClose, anchorEl, open }: Props) {
  const navigate = useNavigate();
  const { logOut, userData } = useSession();
  const displayName = getUserDisplayName(userData);
  const initials = getUserInitials(userData);
  const roleLabel = getUserRoleLabel(userData?.role);
  const canOpenProfile = ROUTES_PERMISSIONS[ROUTES.USER_SETTINGS].includes(userData?.role || '');
  const canChangePassword = ROUTES_PERMISSIONS[ROUTES.CHANGE_PASSWORD].includes(userData?.role || '');

  return (
    <Menu
      anchorEl={anchorEl}
      id='account-menu'
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 260,
            maxWidth: 'calc(100vw - 32px)',
            svg: {
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Avatar
            aria-label={`Avatar de ${displayName}`}
            sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontWeight: 600 }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant='body2' fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
              {userData?.email || displayName}
            </Typography>
            {roleLabel && (
              <Typography variant='caption' color='text.secondary'>
                {roleLabel}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
      <Divider />
      {canOpenProfile && (
        <MenuItem onClick={() => navigate(ROUTES.USER_SETTINGS)}>
          <MdPersonOutline />
          Mi Perfil
        </MenuItem>
      )}
      {canChangePassword && (
        <MenuItem onClick={() => navigate(ROUTES.CHANGE_PASSWORD)}>
          <MdSettings />
          Cambiar contraseña
        </MenuItem>
      )}
      {(canOpenProfile || canChangePassword) && <Divider />}
      <MenuItem
        onClick={() => {
          logOut();
          navigate(ROUTES.INDEX);
        }}
      >
        <MdLogout /> Salir
      </MenuItem>
    </Menu>
  );
}
